/**
 * V102-8-5 — record a self-assessment result from a cell, and review it
 * (ux-adversary run cmmc-self-assessment round 7 `determination.web.html`;
 * ADR-0056 §5, ADR-0046 preparer/approver, owner decision D-UX-2 "peer first,
 * official last").
 *
 *   Record      result (Met · Not met · N/A · No evidence); a gap type is
 *               required on Not met; a rationale unless Met; evidence picked
 *               from this workspace's evidence for the objective
 *               (GET …/determination-options). Supersede (a reviewed result)
 *               and Revise (a sent-back one) reuse the same form.
 *   Hand-off    after recording, the recorder picks the reviewer from
 *               GET …/reviewers (self and other preparers shown disabled with
 *               the reason; the affirming official last) → POST …/request-review.
 *   Review      the program lead / owner / admin who did not prepare it:
 *               Confirm <result> (POST …/review) or Send back with what should
 *               change (POST …/send-back). A sent-back result shows the note and
 *               Revise to its recorder.
 *
 * SoD shows in the UI (the recorder never sees Confirm on their own result) and
 * the server enforces it whatever the UI shows (403 `sod`). Focus returns to
 * the opener after each action, re-applied after the refetch re-mounts it.
 */
import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Checkbox, Field, Radio, Select, Spinner, Textarea } from '@aigovops/design-system';
import {
  ApiError, getDeterminationOptions, getDeterminationReviewers, proposeDetermination, requestDeterminationReview, reviewDetermination,
  sendBackDetermination, supersedeDetermination, type DeterminationJudgment, type ReviewerCandidateRow,
} from '../../lib/api';
import { invalidationMap, queryKeys } from '../../lib/query-keys';
import { fmtWithYear } from '../../lib/cmmc-model';
import { GAP_WORDS, STATUS, type CellHistory, type RecordRow } from '../../lib/cmmc-assess-model';
import { focusAfterSettle } from '../../lib/focus-settle';
import { AskAdmin } from '../AskAdmin';

type Result = DeterminationJudgment['result'];
type Gap = NonNullable<DeterminationJudgment['gap_type']>;

const RESULTS: ReadonlyArray<[Result, string]> = [['met', 'Met'], ['not_met', 'Not met'], ['not_applicable', 'N/A'], ['no_evidence', 'No evidence']];
const GAPS: readonly Gap[] = ['not_documented', 'not_implemented', 'not_operating', 'not_effective'];
const KIND_WORDS: Record<string, string> = {
  artifact: 'Document', document: 'Linked document', check: 'Check', reconciliation: 'Reconciliation', interview: 'Interview', sample: 'Sample', population: 'Population', attestation: 'Attestation',
};
const RATIONALE_LABEL: Record<Result, string> = {
  met: 'Why it’s Met', not_met: 'What the evidence shows', not_applicable: 'Why it doesn’t apply', no_evidence: 'What’s missing',
};
const BASIS_WORDS: Record<ReviewerCandidateRow['basis'], string> = { program_lead: 'Program lead', owner: 'Owner', admin: 'Admin' };
const RECORD_H = 'cmmc-record-h';
const today = () => new Date().toISOString().slice(0, 10);
const resultWord = (r: string) => STATUS[r as keyof typeof STATUS]?.label ?? r;
const refKey = (r: { kind: string; id: string }) => `${r.kind}:${r.id}`;
const parseRefs = (json: string | undefined): Array<{ kind: string; id: string }> => {
  try { const v = JSON.parse(json ?? '[]') as unknown; return Array.isArray(v) ? v as Array<{ kind: string; id: string }> : []; } catch { return []; }
};
const errCode = (e: unknown) => (e instanceof ApiError ? (e.body as { code?: string } | undefined)?.code : undefined);
const errText = (e: unknown, fallback: string) => {
  if (e instanceof ApiError) {
    const b = e.body as { error?: string; detail?: string } | undefined;
    if (e.status === 404) return b?.detail && /evidence/i.test(b.detail) ? 'A cited item is no longer in this workspace · untick it and try again.' : 'This result changed or is gone · reload the page.';
    return b?.error && b.error !== 'Forbidden' ? b.error : b?.detail ?? fallback;
  }
  return e instanceof Error ? e.message : fallback;
};

export interface CellRecordProps {
  slug: string;
  program: string;
  periodId: string;
  /** Workspace route prefix (e.g. "/w/northwind/app") — used by the no-reviewer fallback to
   * link into Team settings when the viewer is themselves the only owner / admin. */
  prefix?: string;
  /** Owner or admin on the workspace (not just the program lead). Lets the no-reviewer fallback
   * offer direct Team links when AskAdmin would have no-one to ask. */
  isAdmin?: boolean;
  objective: { id: string; ref: string };
  assetId: string | null;
  /** "Okta" / "Org-wide". */
  where: string;
  history: CellHistory;
  viewerId: string;
  names?: ReadonlyMap<string, string>;
  /** The cell's label when nothing is recorded ("No evidence", "Not assessed"). */
  emptyLabel: string;
  /** The counted result's own view (gap, why, evidence, recorded / reviewed by). */
  reviewedBody?: ReactNode;
  /** What the empty cell offers besides recording (Upload for 3.x.x / Collect it in Gaps). */
  emptyBody?: ReactNode;
  /** An upload action for the evidence picker's empty state (no dead end). */
  uploadSlot?: ReactNode;
}

type Mode =
  | { kind: 'view' }
  | { kind: 'form'; form: 'new' | 'revise' | 'supersede'; opener: string }
  | { kind: 'pick'; opener: string | null }
  | { kind: 'sendback'; opener: string };

export function CellRecord(p: CellRecordProps) {
  const qc = useQueryClient();
  const { history: h } = p;
  const [mode, setMode] = useState<Mode>({ kind: 'view' });
  const [said, setSaid] = useState('');
  const opts = useQuery({
    queryKey: queryKeys.determinationOptions(p.slug, p.program, p.periodId, p.objective.id, p.assetId),
    queryFn: () => getDeterminationOptions(p.slug, p.program, p.periodId, p.objective.id, p.assetId),
    retry: false, staleTime: 30_000,
  });
  const canRecord = !!opts.data?.can_record;
  const canReview = !!opts.data?.can_review;
  const name = (id: string | null | undefined) => (id ? (id === p.viewerId ? 'you' : p.names?.get(id) ?? 'A team member') : '');
  const Name = (id: string | null | undefined) => { const n = name(id); return n === 'you' ? 'You' : n; };
  // A new cell resets the panel.
  useEffect(() => { setMode({ kind: 'view' }); setSaid(''); }, [p.objective.id, p.assetId]);

  const settle = async (message: string, target: string = RECORD_H) => {
    await Promise.all(invalidationMap['determination:changed'](p.slug).map((queryKey) => qc.invalidateQueries({ queryKey })));
    setMode({ kind: 'view' });
    setSaid(message);
    focusAfterSettle(() => document.getElementById(target));
  };
  const back = (opener: string | null) => { setMode({ kind: 'view' }); focusAfterSettle(() => document.getElementById(opener ?? RECORD_H)); };

  const open = h.open;
  const heading = open ? `${p.where} · Awaiting review` : h.returned ? `${p.where} · Sent back` : h.reviewed ? `${p.where} · ${resultWord(h.reviewed.result)}` : `${p.where} · ${p.emptyLabel}`;

  if (mode.kind === 'form') {
    const base = mode.form === 'revise' ? h.returned : mode.form === 'supersede' ? h.reviewed : null;
    return (
      <section className="gp-cmmc-record" aria-labelledby={RECORD_H} data-part="record" data-state="form">
        <h4 id={RECORD_H} tabIndex={-1}>{mode.form === 'supersede' ? `${p.where} · Record a new result` : mode.form === 'revise' ? `${p.where} · Revise` : `${p.where} · Record result`}</h4>
        <RecordForm {...p} form={mode.form} base={base} evidence={opts.data?.evidence ?? []} evidenceLoading={opts.isLoading}
          onCancel={() => back(mode.opener)}
          onDone={() => void settle('Recorded · choose who reviews it')} />
      </section>
    );
  }

  // Revising a sent-back replacement supersedes the counted result again; a sent-back first result proposes again.
  const recordAction = (form: 'new' | 'revise' | 'supersede', label: string, id: string, primary = false) => (
    <Button id={id} variant={primary ? 'primary' : 'secondary'} compact data-part={`record-${form}`} onClick={() => setMode({ kind: 'form', form, opener: id })}>{label}</Button>
  );
  const askRecorder = (verb: string) => (
    <AskAdmin slug={p.slug} compact verb={verb} what="records this result" subject={`GAC Pro: ${p.objective.ref} · ${p.where}`} roles={['owner', 'admin', 'editor']}
      prefer={h.returned?.proposed_by ?? h.reviewed?.proposed_by ?? null} exclude={p.viewerId} lead="The control owner or the program lead" />
  );

  return (
    <section className="gp-cmmc-record" aria-labelledby={RECORD_H} data-part="record" data-state={open ? 'proposed' : h.returned ? 'sent_back' : h.reviewed ? 'reviewed' : 'empty'}>
      <h4 id={RECORD_H} tabIndex={-1}>{heading}</h4>
      {/* Always mounted, so each action is announced.
          PR #57 R1 a11y Medium #6: aria-atomic so the full status replaces (not appends to) the
          live region when it changes. */}
      <p role="status" aria-atomic="true" className="gp-cmmc-note" data-part="record-said">{said}</p>
      {open ? (
        <PendingReview {...p} row={open} replaces={open.supersedes_id ? h.reviewed : null} earlier={h.earlierReturn} canRecord={canRecord} canReview={canReview}
          mode={mode} setMode={setMode} settle={settle} back={back} name={name} Name={Name} />
      ) : h.returned ? (
        <div className="gp-cmmc-recbox" data-part="sent-back">
          <p className="gp-cmmc-recmeta">{`${Name(h.returned.sent_back!.by)} sent ${h.returned.proposed_by === p.viewerId ? 'your' : `${name(h.returned.proposed_by)}’s`} ${resultWord(h.returned.result)} back · ${fmtWithYear(h.returned.sent_back!.at, today())}`}</p>
          <blockquote className="gp-cmmc-quote" data-part="sent-back-reason">{h.returned.sent_back!.reason}</blockquote>
          <p className="gp-cmmc-recact">
            {canRecord ? recordAction('revise', 'Revise', 'rec-act-revise', true) : askRecorder('revise it')}
            <span className="gp-cmmc-note">Revising makes a new record · it goes to review again</span>
          </p>
        </div>
      ) : null}
      {/* The counted result stands until a replacement is reviewed.
          PR #57 R1 flow High #3: the "counts now" cue is a labelled chip inside the pending box
          (see PendingReview) — not a muted subnote here — so the counted result's weight is not
          lost under the "Awaiting review" heading. We only render the reviewedBody here (the
          detail rows for the counted result sit below the pending box). */}
      {h.reviewed ? p.reviewedBody : null}
      {!open && !h.returned && !h.reviewed ? p.emptyBody : null}
      {!open && !h.returned ? (
        <p className="gp-cmmc-recact" data-part="record-actions">
          {opts.isLoading ? <Spinner label="Checking who can record" />
            : canRecord ? (h.reviewed ? recordAction('supersede', 'Record a new result', 'rec-act-supersede') : recordAction('new', 'Record result', 'rec-act-record', true))
            : h.reviewed ? null : askRecorder('record it')}
          {canRecord && h.reviewed ? <span className="gp-cmmc-note">The current result counts until the new one is reviewed</span> : null}
        </p>
      ) : null}
    </section>
  );
}

// ---- the record form ------------------------------------------------------------------------------

function RecordForm({ slug, program, periodId, objective, assetId, history, form, base, evidence, evidenceLoading, uploadSlot, onCancel, onDone }: CellRecordProps & {
  form: 'new' | 'revise' | 'supersede'; base: RecordRow | null; evidence: ReadonlyArray<NonNullable<RecordRow['evidence']>[number]>; evidenceLoading: boolean;
  onCancel: () => void; onDone: () => void;
}) {
  const idp = `rec-${objective.id.replace(/[^A-Za-z0-9]+/g, '-')}`;
  // Supersede starts blank (a new judgment); Revise starts from the sent-back record.
  const seed = form === 'revise' ? base : null;
  const [result, setResult] = useState<Result | ''>((seed?.result as Result | undefined) ?? '');
  const [gap, setGap] = useState<Gap | ''>((seed?.gap_type as Gap | null | undefined) ?? '');
  const [why, setWhy] = useState(seed?.rationale ?? '');
  const [picked, setPicked] = useState<ReadonlySet<string>>(() => new Set(parseRefs(base?.evidence_refs_json).map(refKey)));
  const [errs, setErrs] = useState<{ result?: string; gap?: string; why?: string }>({});
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // The picker: this workspace's evidence for the objective, plus anything the base record cited that still resolves.
  const items = useMemo(() => {
    const seen = new Map<string, NonNullable<RecordRow['evidence']>[number]>();
    for (const e of evidence) seen.set(refKey(e), e);
    for (const e of base?.evidence ?? []) if (e.title && !seen.has(refKey(e))) seen.set(refKey(e), e);
    return [...seen.values()];
  }, [evidence, base]);
  useEffect(() => {
    // Opening focuses the result (the checked one, else the first).
    const el = document.querySelector<HTMLInputElement>(`#${idp}-form input[name="${idp}-result"]:checked`) ?? document.querySelector<HTMLInputElement>(`#${idp}-form input[name="${idp}-result"]`);
    el?.focus();
  }, [idp]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const next: typeof errs = {};
    if (!result) next.result = 'Pick a result';
    if (result === 'not_met' && !gap) next.gap = 'Pick what’s wrong';
    if (result && result !== 'met' && !why.trim()) next.why = 'Say why, in a sentence';
    setErrs(next);
    if (next.result || next.gap || next.why) {
      const first = next.result ? document.querySelector<HTMLInputElement>(`#${idp}-form input[name="${idp}-result"]`) : document.getElementById(next.gap ? `${idp}-gap` : `${idp}-why`);
      first?.focus();
      return;
    }
    setBusy(true); setErr(null);
    const body: DeterminationJudgment = {
      result: result as Result, gap_type: result === 'not_met' ? gap as Gap : null, rationale: why.trim() || null,
      evidence_refs: items.filter((x) => picked.has(refKey(x))).map(({ kind, id }) => ({ kind, id })),
    };
    try {
      // A sent-back replacement is revised as a replacement of the same counted result.
      const target = form === 'supersede' ? history.reviewed : form === 'revise' && base?.supersedes_id && history.reviewed?.determination_id === base.supersedes_id ? history.reviewed : null;
      if (target) await supersedeDetermination(slug, program, periodId, target.determination_id, body);
      else await proposeDetermination(slug, program, periodId, { ...body, objective_id: objective.id, asset_id: assetId });
      onDone();
    } catch (x) {
      setErr(errCode(x) === 'sod' ? 'You prepared part of this result · someone else must record it.' : errText(x, 'The result could not be recorded.'));
      setBusy(false);
    }
  };

  const toggle = (k: string) => setPicked((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  return (
    <form id={`${idp}-form`} className="gp-cmmc-recform" onSubmit={submit} noValidate aria-labelledby={RECORD_H} data-part="record-form"
      // Escape cancels the form only; it never reaches the panel around it.
      onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onCancel(); } }}>
      {err ? <Alert variant="error">{err}</Alert> : null}
      <fieldset className="gp-cmmc-fieldset" aria-describedby={errs.result ? `${idp}-result-err` : undefined} data-part="result-picker">
        <legend>Result</legend>
        <div className="gp-cmmc-radios">
          {RESULTS.map(([v, label]) => (
            <Radio key={v} id={`${idp}-r-${v}`} name={`${idp}-result`} value={v} label={label} checked={result === v}
              onChange={() => { setResult(v); setErrs((x) => ({ ...x, result: undefined })); }} />
          ))}
        </div>
        {errs.result ? <p id={`${idp}-result-err`} className="gp-field-err" role="alert">{errs.result}</p> : null}
      </fieldset>
      {result === 'not_met' ? (
        <Field id={`${idp}-gap`} label="What’s wrong" required error={errs.gap}>
          <Select value={gap} onChange={(e) => { setGap(e.currentTarget.value as Gap); setErrs((x) => ({ ...x, gap: undefined })); }}>
            <option value="">Choose…</option>
            {GAPS.map((g) => <option key={g} value={g}>{GAP_WORDS[g]}</option>)}
          </Select>
        </Field>
      ) : null}
      {result ? (
        <Field id={`${idp}-why`} label={result === 'met' ? `${RATIONALE_LABEL.met} (optional)` : RATIONALE_LABEL[result]} required={result !== 'met'} error={errs.why}
          hint={result === 'not_applicable' ? 'N/A goes to review like any result' : result === 'no_evidence' ? 'Counts as not assessed, never as Not met' : undefined}>
          <Textarea rows={3} maxLength={4000} value={why} onChange={(e) => { setWhy(e.currentTarget.value); setErrs((x) => ({ ...x, why: undefined })); }} />
        </Field>
      ) : null}
      <fieldset className="gp-cmmc-fieldset" data-part="evidence-picker">
        <legend>Evidence · already collected</legend>
        {evidenceLoading ? <Spinner label="Loading collected evidence" />
          : items.length ? items.map((x) => (
            <Checkbox key={refKey(x)} id={`${idp}-ev-${refKey(x).replace(/[^A-Za-z0-9]+/g, '-')}`} checked={picked.has(refKey(x))} onChange={() => toggle(refKey(x))}
              label={x.title ?? 'Evidence'}
              description={[KIND_WORDS[x.kind] ?? x.kind, x.status ? x.status.charAt(0).toUpperCase() + x.status.slice(1).replace(/_/g, ' ') : null, x.observed_at ? fmtWithYear(x.observed_at, today()) : null].filter(Boolean).join(' · ')} />
          ))
          : <p className="gp-cmmc-note" data-part="evidence-empty">{`Nothing collected for ${objective.ref} yet · `}{uploadSlot ?? 'upload it from Gaps'}</p>}
      </fieldset>
      <span className="gp-actions">
        <Button type="submit" variant="primary" aria-disabled={busy || undefined} data-part="record-submit">
          {busy ? 'Recording…' : form === 'supersede' ? 'Record new result' : form === 'revise' ? 'Record again' : 'Record result'}</Button>
        <Button variant="ghost" onClick={onCancel}>Cancel</Button>
      </span>
      <p className="gp-cmmc-note">Goes to review · you choose the reviewer next</p>
    </form>
  );
}

// ---- awaiting review --------------------------------------------------------------------------------

function PendingReview({ slug, program, periodId, prefix, isAdmin, row, replaces, earlier, viewerId, canRecord, canReview, mode, setMode, settle, back, name, Name }: CellRecordProps & {
  row: RecordRow; replaces: RecordRow | null; earlier: RecordRow | null; canRecord: boolean; canReview: boolean;
  mode: Mode; setMode: (m: Mode) => void; settle: (message: string, target?: string) => Promise<void>; back: (opener: string | null) => void;
  name: (id: string | null | undefined) => string; Name: (id: string | null | undefined) => string;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const mine = row.proposed_by === viewerId;
  const req = row.review_request ?? null;
  const evidence = row.evidence ?? [];
  // The recorder picks the reviewer straight away when nobody has been asked yet.
  const picking = mode.kind === 'pick' || (!req && mine && canRecord && mode.kind === 'view');
  const approve = async () => {
    if (busy) return;
    setBusy(true); setErr(null);
    try {
      await reviewDetermination(slug, program, periodId, row.determination_id);
      await settle(`Confirmed ${resultWord(row.result)} · it counts now`);
    } catch (x) {
      setErr(errCode(x) === 'sod' ? 'You prepared part of this result · another reviewer is needed.' : errText(x, 'The review could not be saved.'));
    } finally { setBusy(false); }
  };
  return (
    <div className="gp-cmmc-recbox" data-part="pending">
      {/* PR #57 R1 flow High #3: when this proposal replaces a counted result, lead with a
         "Counts now" chip so the real-world status is unmistakable while review is pending.
         (The old muted "Replaces · <result>" dl row has been dropped — it under-weighted the
         counted result.) */}
      {replaces ? (
        <p className="gp-cmmc-counts-chip" data-part="counts-now-chip">
          <span className="gp-cmmc-counts-label">Counts now</span>
          <strong data-tone={replaces.result === 'not_met' ? 'red' : replaces.result === 'met' ? 'ok' : undefined}>
            {resultWord(replaces.result)}
          </strong>
          <span className="gp-cmmc-counts-until">— until this proposal is reviewed</span>
        </p>
      ) : null}
      {earlier?.sent_back ? (
        <p className="gp-cmmc-note" data-part="earlier-note">{`Sent back earlier by ${name(earlier.sent_back.by)}: “${earlier.sent_back.reason}”`}</p>
      ) : null}
      <dl className="gp-cmmc-dl">
        <dt>Recorded</dt><dd>{`${resultWord(row.result)}${row.gap_type && row.gap_type !== 'no_evidence' ? ` · ${GAP_WORDS[row.gap_type] ?? row.gap_type}` : ''}`}</dd>
        {row.rationale ? <><dt>{row.result === 'not_applicable' ? 'Why N/A' : 'Why'}</dt><dd>{row.rationale}</dd></> : null}
        <dt>Evidence</dt><dd>{evidence.length ? (
          <ul className="gp-cmmc-cited">{evidence.map((e) => <li key={refKey(e)}>{e.title ?? 'Evidence not found'}</li>)}</ul>
        ) : 'None cited'}</dd>
        <dt>Recorded by</dt><dd>{`${Name(row.proposed_by)} · ${fmtWithYear(row.proposed_at, today())}`}</dd>
      </dl>

      {/* The hand-off: who it waits on, or the picker. */}
      {picking ? (
        <ReviewerPicker slug={slug} program={program} periodId={periodId} prefix={prefix} isAdmin={isAdmin} row={row} viewerId={viewerId} name={name} current={req?.reviewer_user_id ?? null}
          onCancel={mode.kind === 'pick' ? () => back(mode.opener) : null}
          onSent={(who) => void settle(`Sent to ${who} for review`)} />
      ) : req ? (
        <p className="gp-cmmc-recact" data-part="waiting-on">
          <span>{req.reviewer_user_id === viewerId ? `Waiting on you · asked ${fmtWithYear(req.at, today())}` : `With ${name(req.reviewer_user_id)} for review · asked ${fmtWithYear(req.at, today())}`}</span>
          {canRecord && req.reviewer_user_id !== viewerId
            ? <Button id="rec-act-change" compact variant="ghost" data-part="change-reviewer" onClick={() => setMode({ kind: 'pick', opener: 'rec-act-change' })}>Change reviewer</Button> : null}
        </p>
      ) : canReview && !mine ? null /* the reviewer is here: Confirm / Send back below */
        : canRecord ? (
          <p className="gp-cmmc-recact" data-part="waiting-on">
            <span className="gp-cmmc-note">No reviewer asked yet</span>
            <Button id="rec-act-change" compact variant="secondary" data-part="choose-reviewer" onClick={() => setMode({ kind: 'pick', opener: 'rec-act-change' })}>Choose a reviewer</Button>
          </p>
        ) : <p className="gp-cmmc-note" data-part="waiting-on">{`Waiting for ${name(row.proposed_by)} to choose a reviewer`}</p>}

      {/* Review: never the recorder (SoD shown here; the server enforces it). */}
      {canReview && mine ? <p className="gp-cmmc-note" data-part="sod-note">You recorded this · someone else reviews it</p> : null}
      {canReview && !mine ? (
        mode.kind === 'sendback'
          ? <SendBackForm slug={slug} program={program} periodId={periodId} row={row} to={name(row.proposed_by)} onCancel={() => back(mode.opener)} onSent={() => void settle(`Sent back to ${name(row.proposed_by)}`)} />
          : (
            <div className="gp-cmmc-reviewbar" data-part="review-actions">
              <span className="gp-cmmc-note">You review · not the recorder</span>
              <span className="gp-actions">
                <Button id="rec-act-sendback" variant="secondary" compact onClick={() => setMode({ kind: 'sendback', opener: 'rec-act-sendback' })} data-part="send-back">Send back…</Button>
                <Button variant="primary" compact aria-disabled={busy || undefined} onClick={() => void approve()} data-part="approve">{busy ? 'Saving…' : `Confirm ${resultWord(row.result)}`}</Button>
              </span>
            </div>
          )
      ) : null}
      {err ? <p role="alert" className="gp-field-err">{err}</p> : null}
    </div>
  );
}

// ---- reviewer picker ---------------------------------------------------------------------------------

function ReviewerPicker({ slug, program, periodId, prefix, isAdmin, row, viewerId, name, current, onCancel, onSent }: {
  slug: string; program: string; periodId: string; prefix?: string; isAdmin?: boolean;
  row: RecordRow; viewerId: string; name: (id: string | null | undefined) => string;
  current: string | null; onCancel: (() => void) | null; onSent: (who: string) => void;
}) {
  const q = useQuery({
    queryKey: queryKeys.determinationReviewers(slug, program, periodId, row.determination_id),
    queryFn: () => getDeterminationReviewers(slug, program, periodId, row.determination_id), retry: false, staleTime: 30_000,
  });
  const [chosen, setChosen] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const idp = `rv-${row.determination_id.replace(/[^A-Za-z0-9]+/g, '-')}`;
  const candidates = q.data?.candidates ?? [];
  const eligible = candidates.filter((c) => c.eligible);
  const pick = chosen ?? (current && eligible.some((c) => c.user_id === current) ? current : q.data?.routed_to ?? null);
  useEffect(() => {
    // Changing the reviewer focuses the selected person.
    if (onCancel && q.data) document.querySelector<HTMLInputElement>(`#${idp} input:checked`)?.focus();
  }, [onCancel, q.data, idp]);
  if (q.isLoading) return <Spinner label="Finding a reviewer" />;
  if (q.error) return <Alert variant="error"><p>Reviewers could not load.</p><Button onClick={() => void q.refetch()}>Retry</Button></Alert>;
  const why = (c: ReviewerCandidateRow) => {
    if (c.reason === 'preparer') return c.user_id === viewerId ? 'You recorded this · can’t review it' : 'Prepared this result · can’t review it';
    if (c.reason === 'absent') return c.absent_until ? `Away until ${fmtWithYear(c.absent_until, today())}` : 'Away';
    if (c.reason === 'audit_firm') return 'On an audit firm · can’t review your results';
    return c.official ? `${BASIS_WORDS[c.basis]} · affirms the program, so asked last` : BASIS_WORDS[c.basis];
  };
  const send = async (e: FormEvent) => {
    e.preventDefault();
    if (busy || !pick) return;
    // PR #57 R1 a11y Medium #5: client-side guard against submitting an ineligible pick (the
    // Radio is aria-disabled, not disabled, so a mouse click still fires onChange).
    const picked = candidates.find((c) => c.user_id === pick);
    if (!picked || !picked.eligible) {
      setErr(picked ? `${name(pick)} can’t review this · ${why(picked).toLowerCase()}` : 'Choose a reviewer who can take this.');
      return;
    }
    setBusy(true); setErr(null);
    try {
      await requestDeterminationReview(slug, program, periodId, row.determination_id, pick);
      onSent(name(pick));
    } catch (x) {
      setErr(errText(x, 'The review could not be requested.'));
      setBusy(false);
    }
  };
  if (!eligible.length) {
    // PR #57 R1 flow High #4: no dead-end even when the viewer is themselves the only owner /
    // admin. AskAdmin has nobody to ask in that case, so an owner / admin sees direct Team links
    // (assign the program-lead hat / invite a reviewer) instead; others fall through to AskAdmin.
    const reasonLine = candidates.length
      ? `No one can review it now · ${candidates.map((c) => `${name(c.user_id)}: ${why(c).toLowerCase()}`).join(' · ')}`
      : 'No reviewer is set up for this program';
    return (
      <div className="gp-cmmc-recact gp-cmmc-no-reviewer" data-part="no-reviewer">
        <span className="gp-cmmc-note">{reasonLine}</span>
        {isAdmin && prefix ? (
          <span className="gp-cmmc-no-reviewer-acts" data-part="no-reviewer-admin">
            <a className="gp-cmmc-quiet" data-part="assign-lead" href={`${prefix}/settings/team?assign=program-lead&program=${encodeURIComponent(program)}`}>Assign the program-lead hat</a>
            <span className="gp-cmmc-note" aria-hidden="true">·</span>
            <a className="gp-cmmc-quiet" data-part="invite-reviewer" href={`${prefix}/settings/team?invite=1`}>Invite a reviewer</a>
          </span>
        ) : (
          <AskAdmin slug={slug} compact verb="add a reviewer" what="adds a program lead or an admin" subject={`GAC Pro: a reviewer for ${row.objective_id}`} roles={['owner', 'admin']} exclude={viewerId} />
        )}
      </div>
    );
  }
  return (
    <form id={idp} className="gp-cmmc-recform" onSubmit={send} noValidate data-part="reviewer-picker"
      onKeyDown={(e) => { if (e.key === 'Escape' && onCancel) { e.preventDefault(); e.stopPropagation(); onCancel(); } }}>
      <fieldset className="gp-cmmc-fieldset">
        <legend>Who reviews it</legend>
        {q.data?.official_only ? <p className="gp-cmmc-note">{`Only ${name(q.data.routed_to)} can review it now · no peer is free`}</p> : null}
        <div className="gp-cmmc-radios gp-cmmc-radios--stack">
          {candidates.map((c) => {
            const rid = `${idp}-${c.user_id.replace(/[^A-Za-z0-9]+/g, '-')}`;
            const descId = `${rid}-desc`;
            // PR #57 R1 a11y Medium #5: aria-disabled (not `disabled`) so screen readers still
            // read the reason on focus; the submit handler and `pick` resolution refuse to send
            // an ineligible candidate so the UI stays safe. aria-describedby points to the
            // description span (Radio's `descriptionId` wires the id onto it).
            return (
              <Radio key={c.user_id} id={rid} name={`${idp}-who`} value={c.user_id}
                aria-disabled={!c.eligible || undefined}
                aria-describedby={descId}
                descriptionId={descId}
                checked={pick === c.user_id}
                onChange={() => { if (c.eligible) setChosen(c.user_id); }}
                label={c.user_id === viewerId ? `${name(c.user_id)} (you)` : name(c.user_id)} description={why(c)} />
            );
          })}
        </div>
      </fieldset>
      {err ? <p role="alert" className="gp-field-err">{err}</p> : null}
      <span className="gp-actions">
        <Button type="submit" variant="primary" compact aria-disabled={busy || !pick || undefined} data-part="send-for-review">
          {busy ? 'Sending…' : pick ? `Send to ${name(pick)}` : 'Choose a reviewer'}</Button>
        {onCancel ? <Button variant="ghost" compact onClick={onCancel}>Cancel</Button> : null}
      </span>
    </form>
  );
}

// ---- send back ----------------------------------------------------------------------------------------

/** "What should change?" — a reason is required; the recorder sees it with Revise. */
export function SendBackForm({ slug, program, periodId, row, to, onCancel, onSent }: {
  slug: string; program: string; periodId: string; row: Pick<RecordRow, 'determination_id'>; to: string; onCancel: () => void; onSent: () => void;
}) {
  const [why, setWhy] = useState('');
  const [fieldErr, setFieldErr] = useState<string | undefined>();
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const id = `sb-${row.determination_id.replace(/[^A-Za-z0-9]+/g, '-')}`;
  useEffect(() => { document.getElementById(id)?.focus(); }, [id]);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (!why.trim()) { setFieldErr(`Tell ${to} what to change`); document.getElementById(id)?.focus(); return; }
    setBusy(true); setErr(null);
    try {
      await sendBackDetermination(slug, program, periodId, row.determination_id, why.trim());
      onSent();
    } catch (x) {
      setErr(errCode(x) === 'sod' ? 'You prepared part of this result · another reviewer must send it back.' : errText(x, 'It could not be sent back.'));
      setBusy(false);
    }
  };
  return (
    <form className="gp-cmmc-recform" onSubmit={submit} noValidate aria-label={`Send back to ${to}`} data-part="send-back-form"
      onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onCancel(); } }}>
      <Field id={id} label="What should change?" required error={fieldErr} hint={`${to} sees this note with Revise`}>
        <Textarea rows={3} maxLength={2000} value={why} onChange={(e) => { setWhy(e.currentTarget.value); setFieldErr(undefined); }} />
      </Field>
      {err ? <p role="alert" className="gp-field-err">{err}</p> : null}
      <span className="gp-actions">
        <Button type="submit" variant="primary" compact aria-disabled={busy || undefined} data-part="send-back-submit">{busy ? 'Sending…' : `Send back to ${to}`}</Button>
        <Button variant="ghost" compact onClick={onCancel}>Cancel</Button>
      </span>
    </form>
  );
}
